// @flow

import * as React from 'react';
import { bind } from '@ta/common.api';
import { LoadingSpinner } from '@ta/styleguide.loading';
import DataCacher from '@ta/common.data-cacher';
import type { SimpleLink } from './SimpleLinkSection';
import type { PartnerLink } from './PartnerSitesSection';
import type { LegalData } from './Legal';
import type { CurrencyPreview, PosPreview } from './pickers/types';
import type { SocialLink } from './SocialLinksSection';
import DDLogo from './DDLogo';
import DaoDaoLayout from './layouts/DaoDaoLayout';
import type { DDFooterData } from './layouts/DaoDaoLayout';

type FooterLinks = {
  aboutLinks: SimpleLink[],
  applinks: SimpleLink[],
  businessLinks: SimpleLink[],
  exploreLinks: SimpleLink[],
  partnerLinks: PartnerLink[],
  socialLinks: SocialLink[],
};

/** There is no compilation-enforced contract between this type and the restful API response,
 *  but for reference this is what we expect back from the footer endpoint. */
export type FooterData = {
  /** Lots of links */
  links: FooterLinks,

  /** Info for legal section. */
  legal: LegalData,

  /** The data needed to show a preview (clickable target) for the currency picker. */
  currencyPreview: CurrencyPreview,

  /** The data needed to show a preview (clickable target) for the POS picker. */
  posPreview: PosPreview,
};

type Props = {
  footerApi: {
    data: ?FooterData,
  },
  ddFooterApi: {
    data: ?DDFooterData,
  },
};

const DaoDaoFooter = ({ footerApi, ddFooterApi }: Props) => {
  const footerData = footerApi.data;
  const ddFooterData = ddFooterApi.data;

  if (!footerData || !ddFooterData) {
    return <LoadingSpinner />;
  }

  return <DaoDaoLayout footerData={footerData} daodaoFooterData={ddFooterData} />;
};

const getFooterParams = state => {
  const { initialServletName, device } = state.meta;
  const { locale, currencyCode } = state.i18n;
  const { mcid } = state.tracking;
  const viewportCategory = state.meta.forceFullSite ? 'DESKTOP' : device.viewportCategory;

  const paramMap = {
    servlet: initialServletName,
    locale,
    currencyCode,
    mcid,
    geoId: state.page.geoId ? state.page.geoId : null,
    detailId: state.page.detailId ? state.page.detailId : null,
    viewportCategory,
  };

  // Filter out null and undefined values
  const filteredParams = {};
  Object.keys(paramMap).forEach(key => {
    if (paramMap[key] !== null && typeof paramMap[key] !== 'undefined') {
      filteredParams[key] = paramMap[key];
    }
  });

  return new URLSearchParams(filteredParams);
};

const DDFooterApiBinding = bind<Props, {||}, _, _, _, _>((state, stage) => {
  const footerApiCall = stage(`/data/1.0/brand/footer?${getFooterParams(state).toString()}`);
  const ddFooterApiCall = stage(
    `/data/1.0/daodao/brand/footer?${getFooterParams(state).toString()}`,
  );

  DDLogo.stage(state, stage);

  return () => ({
    footerApi: footerApiCall.get(),
    ddFooterApi: ddFooterApiCall.get(),
  });
})(DataCacher()(DaoDaoFooter));

export default DDFooterApiBinding;
